import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  ArrowRight,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isSameDay, getDay } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    postcode: "",
    project_type: "General Tiling Quote",
    notes: ""
  });

  const projectTypes = [
    "Bathroom Tiling",
    "Kitchen Tiling",
    "Floor Tiling",
    "Bathroom Renovation",
    "General Tiling Quote"
  ];

  // Fetch calendar availability on mount
  useEffect(() => {
    fetchAvailability();
  }, []);

  // Fetch available time slots when date is selected
  useEffect(() => {
    if (selectedDate && availability) {
      generateTimeSlots();
    }
  }, [selectedDate, availability]);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/calendar/availability`);
      // Extract blocked dates as a list
      const blockedList = response.data.availability
        ?.filter(a => !a.available && a.reason === "blocked")
        ?.map(a => ({ date: a.date })) || [];
      
      setAvailability({
        working_hours: response.data.working_hours,
        blocked_dates: blockedList,
        slot_duration: response.data.working_hours?.slot_duration || 60,
        availability: response.data.availability
      });
    } catch (error) {
      console.error("Error fetching availability:", error);
      // Use defaults if fetch fails
      setAvailability({
        working_hours: {
          monday: { enabled: true, start: "09:00", end: "17:00" },
          tuesday: { enabled: true, start: "09:00", end: "17:00" },
          wednesday: { enabled: true, start: "09:00", end: "17:00" },
          thursday: { enabled: true, start: "09:00", end: "17:00" },
          friday: { enabled: true, start: "09:00", end: "17:00" },
          saturday: { enabled: true, start: "09:00", end: "13:00" },
          sunday: { enabled: false, start: "09:00", end: "17:00" }
        },
        blocked_dates: [],
        slot_duration: 60
      });
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlots = () => {
    if (!selectedDate || !availability) return;

    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[selectedDate.getDay()];
    const dayConfig = availability.working_hours?.[dayName];

    if (!dayConfig?.enabled) {
      setAvailableSlots([]);
      return;
    }

    const startHour = parseInt(dayConfig.start.split(":")[0]);
    const endHour = parseInt(dayConfig.end.split(":")[0]);
    const slots = [];

    for (let h = startHour; h < endHour; h++) {
      slots.push(`${h.toString().padStart(2, "0")}:00`);
    }

    setAvailableSlots(slots);
    // Clear selected time if it's no longer available
    if (selectedTime && !slots.includes(selectedTime)) {
      setSelectedTime("");
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Past dates
    if (date < today) return true;
    
    // Check if day is disabled in working hours
    if (availability?.working_hours) {
      const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const dayName = dayNames[date.getDay()];
      const dayConfig = availability.working_hours[dayName];
      if (!dayConfig?.enabled) return true;
    }
    
    // Check blocked dates
    if (availability?.blocked_dates) {
      const dateStr = format(date, "yyyy-MM-dd");
      if (availability.blocked_dates.some(b => b.date === dateStr)) return true;
    }
    
    return false;
  };

  const fetchAvailableSlots = async () => {
    // This is now handled by generateTimeSlots
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        booking_date: format(selectedDate, "yyyy-MM-dd"),
        booking_time: selectedTime
      });
      setSubmitted(true);
      toast.success("Quote appointment booked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-20" data-testid="booking-success">
        <div className="container-custom max-w-2xl">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Quote Appointment Booked!</h1>
            <p className="text-gray-600 mb-6">
              We've received your booking for {format(selectedDate, "EEEE, MMMM d, yyyy")} at {selectedTime}.
              We'll be in touch shortly to confirm.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600"><strong>Name:</strong> {formData.name}</p>
              <p className="text-sm text-gray-600"><strong>Phone:</strong> {formData.phone}</p>
              <p className="text-sm text-gray-600"><strong>Project:</strong> {formData.project_type}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline">Back to Home</Button>
              </Link>
              <Link to="/estimate">
                <Button className="bg-primary hover:bg-primary/90">
                  Get Instant Estimate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="booking-page">
      {/* Header */}
      <div className="container-custom max-w-4xl mb-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <CalendarIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Free Quote Appointment</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Book Your Quote Appointment
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose a convenient time for us to visit and provide a detailed, no-obligation quote for your tiling project.
          </p>
        </div>
      </div>

      {/* Booking Form */}
      <div className="container-custom max-w-4xl">
        <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-8">
          {/* Left: Calendar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Select Date
            </h2>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={isDateDisabled}
              className="rounded-md border mx-auto"
            />

            {selectedDate && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  Select Time
                </h3>
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          selectedTime === time
                            ? "bg-primary text-white border-primary"
                            : "border-gray-200 text-gray-700 hover:border-primary hover:text-primary"
                        }`}
                        data-testid={`time-slot-${time}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-amber-50 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-amber-700">No available times on this date</p>
                    <p className="text-xs text-amber-600">Please select a different date</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Contact Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Your Details
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="07123 456789"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="postcode">Postcode *</Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="M1 1AA"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="project_type">Project Type</Label>
                <select
                  id="project_type"
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  {projectTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Tell us about your project..."
                  rows={3}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 h-12"
                disabled={loading || !selectedDate || !selectedTime}
              >
                {loading ? "Booking..." : "Book Quote Appointment"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Free, no-obligation quote. We'll confirm your appointment within 24 hours.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingPage;
